/*
	bf_turnlanding.ts
    Copyright (c) 2013-present Human Cube Inc.

    Does all aircraft landing.  Must all be synchronous and not involve any external DB
    or file access.  Also does not access any external user data.
    These restrictions are to allow it to be re-used in other environments such as the client.

	Notes:
		- Players can land on allies land but not allies carriers.
        - A player or ally must have owned the zone this and last turn to be able to land there.
*/

import BF from '../bfconst1';
import bfH from '../bf_helper';
import { Game, PlayerLandings, PlayerNormalTurn } from '../gameTypes';
import bfTurnReplay from './bf_turnreplay';
import { confirmOrFixXY, getNewZoneXY } from '../bf_hitmap';


export function doAircraftLanding (game: Game, turns: PlayerNormalTurn[]) {
  let distance = bfH.calcDistanceTable(game);
  for(let playerIndex = 0; playerIndex <= game.players; playerIndex++) { // All and 0.
    let landTurn: PlayerLandings = {};
    if( turns[playerIndex] && turns[playerIndex].land && bfH.isObject(turns[playerIndex].land) )
      landTurn = turns[playerIndex].land;
    landPlayerAircraft(game, distance, landTurn, playerIndex);
  }
}


function compareRangeDescending (a: number[], b: number[]) {
  // Descending order (high to low).
  if( a[BF.FORCE_INDEX_MOVE_POINTS] > b[BF.FORCE_INDEX_MOVE_POINTS] )
      return -1;
  if( a[BF.FORCE_INDEX_MOVE_POINTS] < b[BF.FORCE_INDEX_MOVE_POINTS] )
      return 1;
  return 0;
}

function landPlayerAircraft (
  game: Game, distance: number[][], turnLand: PlayerLandings, playerIndex: number,
) {
    // Attempts to land all of a player's aircraft forces.  Crashes or splashes those that do not.
    let zones = game.zones;
    let landingPoints = []; // Array of points for each zone.
    let needLanding = [];   // Array of forces that need landing.

    let allyBits = game.fixedAlly[playerIndex];

    for(let zoneID = 0; zoneID <= zones; zoneID++) {
        landingPoints[zoneID] = 0;
        if( bfH.isLand(game, zoneID) )
            if( (1 << game.zoneOwner[zoneID]) & allyBits )
                if( (1 << game.zoneOwnerPrev[zoneID]) & allyBits )
                    landingPoints[zoneID] = 2000000000;   // Unlimited landing.
    }

    let len = game.force[playerIndex].length;
    for(let j = 0; j < len; j++) {
        let force = game.force[playerIndex][j];
        if( force[BF.FORCE_INDEX_ZONE] > 0 && force[BF.FORCE_INDEX_ZONE] <= zones ) {
            if( game.forceFlags[force[BF.FORCE_INDEX_TYPE]] &
                    (BF.FORCE_FLAG_MUST_LAND_FRIENDLY | BF.FORCE_FLAG_MUST_LAND_ANY) )
                needLanding.push(force);   // This force needs to land.
            else landingPoints[force[BF.FORCE_INDEX_ZONE]] +=
                                            game.forceCarrierCapacity[force[BF.FORCE_INDEX_TYPE]];
        }
    }

    // Sort the needLanding from most to least range remaining as we consume from the end:
    needLanding.sort(compareRangeDescending);

    // Handle player specified landing requests first:
    if( turnLand && bfH.isObject(turnLand) ) {
        for(let key in turnLand) {
            let forceID = bfH.convertToInt(key);
            let item = turnLand[key];
            if( forceID && item && Array.isArray(item) && item.length === 3 ) {
                let zoneID = item[0];
                let x = item[1];
                let y = item[2];

                for(let j = needLanding.length - 1; j >= 0; j--) {
                    if( needLanding[j][BF.FORCE_INDEX_ID] === forceID ) {
                        let carrierSpace =
                                game.forceCarrierSpace[needLanding[j][BF.FORCE_INDEX_TYPE]];
                        if( (landingPoints[zoneID] >= 1000000000 ||
                                (carrierSpace >= 0 && landingPoints[zoneID] >= carrierSpace)) &&
                                needLanding[j][BF.FORCE_INDEX_MOVE_POINTS] >=
                                     distance[needLanding[j][BF.FORCE_INDEX_ZONE]][zoneID] ) {
                            // Do successful landing:
                            bfTurnReplay.land(game, forceID);
                            const pos = confirmOrFixXY(game, zoneID, x, y);
                            needLanding[j][BF.FORCE_INDEX_ZONE_RETREAT] = 0;  // Landing no retreat.
                            needLanding[j][BF.FORCE_INDEX_ZONE] = zoneID;
                            needLanding[j][BF.FORCE_INDEX_X] = pos.x;
                            needLanding[j][BF.FORCE_INDEX_Y] = pos.y;
                            needLanding[j][BF.FORCE_INDEX_MOVE_POINTS] = 0; // Stop after landing.
                            landingPoints[zoneID] -= carrierSpace;
                            needLanding.splice(j, 1);   // Remove from needLanding list.
                        }
                        else {
                            // Prevent landing to unsafe spaces even if requested, auto-landing
                            // will attempt to land them safely instead.
                        }
                        break;
                    }
                }
            }
        }
    }

    // First attempt to land all remaining in same zone of unlimited landing if possible:
    for(let j = needLanding.length - 1; j >= 0; j--) {
        let zoneID = needLanding[j][BF.FORCE_INDEX_ZONE];
        if( landingPoints[zoneID] >= 1000000000 ) {
            // Do successful same zone landing.
            // No replay needed as they are not moving out of their current zone, nor changing x,y.
            needLanding[j][BF.FORCE_INDEX_ZONE_RETREAT] = 0;  // Landing no retreat.
            needLanding[j][BF.FORCE_INDEX_MOVE_POINTS] = 0; // No further movement after landing.
            needLanding.splice(j, 1);   // Remove from needLanding list.
        }
    }

    // For force types that can land on enemy land, see if current zone is land:
    for(let j = needLanding.length - 1; j >= 0; j--) {
        if( game.forceFlags[needLanding[j][BF.FORCE_INDEX_TYPE]] & BF.FORCE_FLAG_MUST_LAND_ANY ) {
            let zoneID = needLanding[j][BF.FORCE_INDEX_ZONE];
            if( bfH.isLand(game, zoneID) ) {
                // Do successful same zone landing.
                // No replay needed as they are not moving out of their current zone, nor changing x,y.
                needLanding[j][BF.FORCE_INDEX_ZONE_RETREAT] = 0;  // Landing no retreat.
                needLanding[j][BF.FORCE_INDEX_MOVE_POINTS] = 0; // No further movement after landing.
                needLanding.splice(j, 1);   // Remove from needLanding list.
            }
        }
    }

    // Next attempt to land all remaining in same zone on a carrier if possible (this probably what
    // they want):
    for(let j = needLanding.length - 1; j >= 0; j--) {
        let carrierSpace = game.forceCarrierSpace[needLanding[j][BF.FORCE_INDEX_TYPE]];
        let zoneID = needLanding[j][BF.FORCE_INDEX_ZONE];
        if( carrierSpace >= 0 && landingPoints[zoneID] >= carrierSpace ) {
            // Do successful carrier landing.
            // No replay needed as they are not moving out of their current zone, nor changing x,y.
            needLanding[j][BF.FORCE_INDEX_ZONE_RETREAT] = 0;  // Landing no retreat.
            needLanding[j][BF.FORCE_INDEX_MOVE_POINTS] = 0; // No further movement after landing.
            landingPoints[zoneID] -= carrierSpace;
            needLanding.splice(j, 1);   // Remove from needLanding list.
        }
    }

    // Attempt to land all remaining on unlimited spots first (land zones as opposed to carriers):
    for(let j = needLanding.length - 1; j >= 0; j--) {
        for(let range = 1; range <= needLanding[j][BF.FORCE_INDEX_MOVE_POINTS]; range++) {
            for(let zoneID = 1; zoneID <= zones; zoneID++) {
                if( landingPoints[zoneID] >= 1000000000 &&
                        distance[needLanding[j][BF.FORCE_INDEX_ZONE]][zoneID] <= range ) {
                    // Do successful landing:
                    bfTurnReplay.land(game, needLanding[j][BF.FORCE_INDEX_ID]);
                    const pos = getNewZoneXY(game, zoneID);
                    needLanding[j][BF.FORCE_INDEX_ZONE_RETREAT] = 0;  // Landing no retreat.
                    needLanding[j][BF.FORCE_INDEX_ZONE] = zoneID;
                    needLanding[j][BF.FORCE_INDEX_X] = pos.x;
                    needLanding[j][BF.FORCE_INDEX_Y] = pos.y;
                    needLanding[j][BF.FORCE_INDEX_MOVE_POINTS] = 0; // Stop after landing.
                    needLanding.splice(j, 1);   // Remove from needLanding list.

                    range = 1000;  // break out of outer for loop.
                    break;
                }
            }
            if( range === 1000 )
                break;    // break out of outer for loop. (else for loop range check will fail)
        }
    }

    // Attempt to land all remaining on any carrier spots left (closest first):
    for(let j = needLanding.length - 1; j >= 0; j--) {
        let carrierSpace = game.forceCarrierSpace[needLanding[j][BF.FORCE_INDEX_TYPE]];
        if( carrierSpace >= 0 ) {
            for(let range = 1; range <= needLanding[j][BF.FORCE_INDEX_MOVE_POINTS]; range++) {
                for(let zoneID = 1; zoneID <= zones; zoneID++) {
                    if(landingPoints[zoneID] >= carrierSpace &&
                            distance[needLanding[j][BF.FORCE_INDEX_ZONE]][zoneID] <= range ) {
                        // Do successful landing:
                        bfTurnReplay.land(game, needLanding[j][BF.FORCE_INDEX_ID]);
                        const pos = getNewZoneXY(game, zoneID);
                        needLanding[j][BF.FORCE_INDEX_ZONE_RETREAT] = 0;  // Landing no retreat.
                        needLanding[j][BF.FORCE_INDEX_ZONE] = zoneID;
                        needLanding[j][BF.FORCE_INDEX_X] = pos.x;
                        needLanding[j][BF.FORCE_INDEX_Y] = pos.y;
                        needLanding[j][BF.FORCE_INDEX_MOVE_POINTS] = 0; // Stop after landing.
                        landingPoints[zoneID] -= carrierSpace;
                        needLanding.splice(j, 1);   // Remove from needLanding list.

                        range = 1000;  // break out of outer for loop.
                        break;
                    }
                }
                if( range === 1000 )
                    break;    // break out of outer for loop. (else for loop range check will fail)
            }
        }
    }

    // Finally, for force types that can land on enemy land, just find the closest land zone:
    for(let j = needLanding.length - 1; j >= 0; j--) {
        if( game.forceFlags[needLanding[j][BF.FORCE_INDEX_TYPE]] & BF.FORCE_FLAG_MUST_LAND_ANY ) {
            let range = needLanding[j][BF.FORCE_INDEX_MOVE_POINTS];
            let forceZoneID = needLanding[j][BF.FORCE_INDEX_ZONE];
            for(let n = 1; n <= range; n++) {
                for(let zoneID = 1; zoneID <= zones; zoneID++) {
                    if( bfH.isLand(game, zoneID) && distance[forceZoneID][zoneID] === n ) {
                        // Do successful landing:
                        bfTurnReplay.land(game, needLanding[j][BF.FORCE_INDEX_ID]);
                        const pos = getNewZoneXY(game, zoneID);
                        needLanding[j][BF.FORCE_INDEX_ZONE_RETREAT] = 0;  // Landing no retreat.
                        needLanding[j][BF.FORCE_INDEX_ZONE] = zoneID;
                        needLanding[j][BF.FORCE_INDEX_X] = pos.x;
                        needLanding[j][BF.FORCE_INDEX_Y] = pos.y;
                        needLanding[j][BF.FORCE_INDEX_MOVE_POINTS] = 0; // Stop after landing.
                        needLanding.splice(j, 1);   // Remove from needLanding list.

                        range = 1000;  // break out of outer for loop.
                        break;
                    }
                }
                if( range === 1000 )
                    break;    // break out of outer for loop. (else for loop range check will fail)
            }
        }
    }


    // Splash or crash any remaining forces that did not land successfully:
    for(let j = needLanding.length - 1; j >= 0; j--) {
        if( bfH.isWater(game, needLanding[j][BF.FORCE_INDEX_ZONE]) ) {
            bfTurnReplay.splash(game, needLanding[j][BF.FORCE_INDEX_ID]);
            needLanding[j][BF.FORCE_INDEX_ZONE] = BF.FORCE_ZONE_SPLASH;
        }
        else {
            bfTurnReplay.crash(game, needLanding[j][BF.FORCE_INDEX_ID]);
            needLanding[j][BF.FORCE_INDEX_ZONE] = BF.FORCE_ZONE_CRASH;
        }
        needLanding[j][BF.FORCE_INDEX_ZONE_RETREAT] = 0; // Dead, no retreat.
        needLanding[j][BF.FORCE_INDEX_MOVE_POINTS] = 0; // No further movement after spl/cr-ashing.
    }
}
